import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";

const SYM = { GBP: "£", USD: "$", EUR: "€", AUD: "A$", CAD: "C$" };

export const money = (v, unit) => {
  if (v === null || v === undefined) return "—";
  const cur = (unit || "").split("_")[0];
  if (SYM[cur]) return `${SYM[cur]}${v.toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
  if (unit === "percent") return `${v}%`;
  if (unit === "years") return `${v} years`;
  if (unit === "months") return `${v} months`;
  return String(v);
};

const shortCohort = (c) => (/^not yet confirmed/i.test(c) ? "not yet confirmed" : c);

export const isReported = (f) => f && f.availability === "reported";

export const KindTag = ({ kind, testId }) => {
  const s = {
    graduate_programme_outcome: ["Graduate outcome", "bg-emerald-50 text-emerald-800 border-emerald-200"],
    occupation_wide_wage: ["All workers in occupation", "bg-sky-50 text-sky-800 border-sky-200"],
    loan_rule: ["Official loan rule", "bg-stone-100 text-stone-700 border-stone-300"],
  }[kind];
  return <span data-testid={testId} className={`inline-block rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${s[1]}`}>{s[0]}</span>;
};

export function Notes({ testId, children, label = "Sources & data notes", className = "mt-4" }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className={className} data-testid={testId}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        className="-ml-1 inline-flex min-h-[44px] items-center gap-1.5 rounded px-1 text-xs font-medium text-[#002147] underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002147] focus-visible:ring-offset-2">
        <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        {open ? `Hide ${label.toLowerCase()}` : label}
      </button>
      <div id={id} role="region" aria-label={label} hidden={!open} data-testid={`${testId}-panel`}
        className="mt-1 space-y-2 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-[11px] leading-relaxed text-stone-600">
        {children}
      </div>
    </div>
  );
}

export function SourceLine({ f, sources }) {
  const s = sources[f.source_id];
  if (!s) return <span>Source missing</span>;
  return (
    <>
      <a href={s.url} target="_blank" rel="noreferrer" className="underline decoration-stone-300 underline-offset-2 hover:text-[#002147]">{s.publisher}</a>
      {f.period ? ` · ${f.period}` : ""}{s.retrieved_at ? ` · checked ${s.retrieved_at}` : " · retrieval date not recorded"}
      {f.quality ? ` · ${f.quality}` : ""}
      {f.checks?.value_found_in_source ? " · value matched official response" : f.status === "auto_accepted" ? " · wording matched on source" : " · pending check"}
    </>
  );
}

export function FigureNote({ f, sources, testId, extra, showNotes }) {
  return (
    <li data-testid={testId}>
      <span className="font-medium text-stone-800">{f.label}{extra ? ` (${extra})` : ""}: </span>
      {isReported(f) ? <>{f.coverage ? `${f.coverage}. ` : ""}{f.cohort && f.cohort !== shortCohort(f.cohort) ? `Cohort: ${f.cohort}. ` : ""}<SourceLine f={f} sources={sources} />{showNotes && sources[f.source_id]?.notes ? <span className="block text-stone-500">{sources[f.source_id].notes}</span> : null}</> : `Not available. ${f.availability_note || ""}`}
    </li>
  );
}

export const NotAvailable = ({ testId, className = "" }) => (
  <span data-testid={testId} className={`inline-block rounded border border-dashed border-stone-300 px-2 py-0.5 text-xs italic text-stone-500 ${className}`}>Data not available</span>
);

export function FigureCard({ f, testId }) {
  if (!isReported(f)) {
    return (
      <div data-testid={testId} className="rounded-xl border border-dashed border-stone-300 px-5 py-4">
        <KindTag kind={f.kind} testId={`${testId}-kind`} />
        <p className="mt-3 text-sm text-stone-700">{f.label}</p>
        <NotAvailable testId={`${testId}-na`} className="mt-2" />
      </div>
    );
  }
  return (
    <div data-testid={testId} className="rounded-xl border border-stone-200 bg-white p-5">
      <KindTag kind={f.kind} testId={`${testId}-kind`} />
      <p className="mt-3 text-sm text-stone-600">{f.label}</p>
      <p data-testid={`${testId}-value`} className="mt-1 font-editorial text-3xl text-stone-900">{money(f.value, f.unit)}</p>
      {f.p25 !== null && f.p25 !== undefined && (
        <p className="text-xs text-stone-500">{/official/.test(f.statistic) ? "Typical range (middle half of graduates)" : "Middle half of courses"}: {money(f.p25, f.unit)} to {money(f.p75, f.unit)}</p>
      )}
      <p className="mt-3 text-xs text-stone-600">{f.population}.</p>
      <p data-testid={`${testId}-period`} className="mt-1 text-xs text-stone-500">{f.cohort ? `Cohort: ${shortCohort(f.cohort)}` : f.period}</p>
    </div>
  );
}
